import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import type {
  ActivatedRouteSnapshot,
  CanActivateFn,
  RouterStateSnapshot,
} from '@angular/router';
import { Router } from '@angular/router';
import {
  navigateToPaidWallContactUs,
  resolvePaidWallContext,
} from '@indutrust/shared-util';
import { filter, first, map } from 'rxjs/operators';
import { AuthSessionStore } from '../+state';

/**
 * Route guard ensuring the authenticated user's company holds a paid
 * licence (`Company.status === 'paid'`) — or that the user is an `admin`,
 * who bypasses the paywall.
 *
 * Per `docs/product/05-roles-and-permissions.md` R2 and `15-product-invariants.mdc`
 * §5, every paid-only capability is a deliberate **conversion wall**: the
 * route MUST be guarded centrally rather than soft-disabled in components.
 * Blocked users are redirected to `/contact-us` with a sales-demo inquiry
 * pre-filled from optional route `data.paidWall` context.
 *
 * Reads {@link AuthSessionStore.isPaid} internally — do not duplicate
 * `company()?.status === 'paid'` at call sites.
 *
 * Unauthenticated requests are expected to be handled upstream by
 * {@link authGuard}.
 *
 * @returns An {@link import('rxjs').Observable} resolving to `true` for paid
 *   companies and admins, `false` otherwise (with a contextual redirect to
 *   `/contact-us`).
 * @example
 * import { Routes } from '@angular/router';
 * import { authGuard, paidGuard } from '@indutrust/auth-data';
 *
 * export const routes: Routes = [
 *   {
 *     path: 'search',
 *     canActivate: [authGuard, paidGuard],
 *     data: {
 *       paidWall: { family: 'discovery', featureId: 'search' },
 *     },
 *     loadChildren: async () =>
 *       import('@indutrust/search-feature').then((m) => m.libSearchRoutes),
 *   },
 * ];
 */
export const paidGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
) => {
  const authStore = inject(AuthSessionStore);
  const router = inject(Router);

  return toObservable(authStore.authenticationInitialized).pipe(
    filter((initialized): boolean => initialized),
    first(),
    map(() => {
      if (authStore.isAdmin() || authStore.isPaid()) {
        return true;
      }

      const paidWall = resolvePaidWallContext(route.data['paidWall']);
      navigateToPaidWallContactUs(
        router,
        paidWall,
        state.url,
        'Navigation to paid-wall contact us failed'
      );
      return false;
    })
  );
};
